import * as z from "zod";
import constants from "~~/constants";

export const signinSchema = () => {
  const { t } = useI18n();
  const emailField = t("labels.email");
  const passwordField = t("labels.password");

  return z.object({
    email: z
      .string({
        message: getLocalizedValidationMessage(
          emailField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      })
      .nonempty(
        getLocalizedValidationMessage(
          emailField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      )
      .email({
        message: getLocalizedValidationMessage(
          emailField,
          constants.VALIDATION_TYPES.EMAIL,
        ),
      }),
    password: z
      .string({
        message: getLocalizedValidationMessage(
          passwordField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      })
      .nonempty(
        getLocalizedValidationMessage(
          passwordField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      ),
  });
};

export const changePasswordSchema = () => {
  const { t } = useI18n();
  const newPasswordLabel = t("labels.new-password");
  const currentPasswordField = t("labels.current-password");
  const confirmPasswordField = t("labels.confirm-password");
  const passwordsField = t("labels.passwords");

  return z
    .object({
      currentPassword: z
        .string({
          message: getLocalizedValidationMessage(
            currentPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .min(1, {
          message: getLocalizedValidationMessage(
            currentPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        }),
      newPassword: z
        .string({
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .nonempty(
          getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        )
        .min(8, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.MIN_LENGTH,
            {
              minLength: 8,
            },
          ),
        })
        .regex(/[A-Z]/, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.CONTAINS_UPPERCASE,
          ),
        })
        .regex(/[a-z]/, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.CONTAINS_LOWERCASE,
          ),
        })
        .regex(/[0-9]/, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.CONTAINS_NUMBER,
          ),
        })
        .regex(/[^A-Za-z0-9]/, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.CONTAINS_SPECIAL,
          ),
        }),
      confirmPassword: z
        .string({
          message: getLocalizedValidationMessage(
            confirmPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .min(1, {
          message: getLocalizedValidationMessage(
            confirmPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: getLocalizedValidationMessage(
        passwordsField,
        constants.VALIDATION_TYPES.MATCH,
      ),
      path: ["confirmPassword"],
    })
    .refine((data) => data.newPassword !== data.currentPassword, {
      message: getLocalizedValidationMessage(
        newPasswordLabel,
        constants.VALIDATION_TYPES.DIFFERENT_FROM_CURRENT,
      ),
      path: ["newPassword"],
    });
};

export const nameUpdateSchema = () => {
  const { t } = useI18n();
  const firstNameField = t("labels.first-name");
  const lastNameField = t("labels.last-name");

  return z.object({
    firstName: z
      .string({
        message: getLocalizedValidationMessage(
          firstNameField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      })
      .nonempty(
        getLocalizedValidationMessage(
          firstNameField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      )
      .min(
        3,
        getLocalizedValidationMessage(
          firstNameField,
          constants.VALIDATION_TYPES.MIN_LENGTH,
          {
            minLength: 3,
          },
        ),
      )
      .max(
        50,
        getLocalizedValidationMessage(
          firstNameField,
          constants.VALIDATION_TYPES.MAX_LENGTH,
          {
            maxLength: 50,
          },
        ),
      ),
    lastName: z
      .string({
        message: getLocalizedValidationMessage(
          firstNameField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      })
      .nonempty(
        getLocalizedValidationMessage(
          firstNameField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      )
      .min(
        3,
        getLocalizedValidationMessage(
          firstNameField,
          constants.VALIDATION_TYPES.MIN_LENGTH,
          {
            minLength: 3,
          },
        ),
      )
      .max(
        50,
        getLocalizedValidationMessage(
          firstNameField,
          constants.VALIDATION_TYPES.MAX_LENGTH,
          {
            maxLength: 50,
          },
        ),
      ),
  });
};

export const findAccountSchema = () => {
  const { t } = useI18n();
  const emailField = t("labels.email");

  return z.object({
    email: z
      .string({
        message: getLocalizedValidationMessage(
          emailField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      })
      .nonempty(
        getLocalizedValidationMessage(
          emailField,
          constants.VALIDATION_TYPES.REQUIRED,
        ),
      )
      .email({
        message: getLocalizedValidationMessage(
          emailField,
          constants.VALIDATION_TYPES.EMAIL,
        ),
      }),
  });
};

export const resetPasswordSchema = () => {
  const { t } = useI18n();
  const newPasswordLabel = t("labels.new-password");
  const confirmPasswordField = t("labels.confirm-password");
  const passwordsField = t("errors.passswords-not-match");

  return z
    .object({
      newPassword: z
        .string({
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .nonempty(
          getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        )
        .min(8, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.MIN_LENGTH,
            {
              minLength: 8,
            },
          ),
        })
        .regex(/[A-Z]/, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.CONTAINS_UPPERCASE,
          ),
        })
        .regex(/[a-z]/, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.CONTAINS_LOWERCASE,
          ),
        })
        .regex(/[0-9]/, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.CONTAINS_NUMBER,
          ),
        })
        .regex(/[^A-Za-z0-9]/, {
          message: getLocalizedValidationMessage(
            newPasswordLabel,
            constants.VALIDATION_TYPES.CONTAINS_SPECIAL,
          ),
        }),
      confirmPassword: z
        .string({
          message: getLocalizedValidationMessage(
            confirmPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .min(1, {
          message: getLocalizedValidationMessage(
            confirmPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: getLocalizedValidationMessage(
        passwordsField,
        constants.VALIDATION_TYPES.MATCH,
      ),
    });
};
export const profileSetupSchema = () => {
  const { t } = useI18n();
  const firstNameField = t("labels.first-name");
  const lastNameField = t("labels.last-name");
  const temporaryPasswordField = t("labels.temporary-password");
  const newPasswordField = t("labels.new-password");
  const confirmPasswordField = t("labels.confirm-password");
  const passwordsField = t("labels.passwords");

  return z
    .object({
      firstName: z
        .string({
          message: getLocalizedValidationMessage(
            firstNameField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .nonempty(
          getLocalizedValidationMessage(
            firstNameField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        )
        .min(3, {
          message: getLocalizedValidationMessage(
            firstNameField,
            constants.VALIDATION_TYPES.MIN_LENGTH,
            {
              minLength: 3,
            },
          ),
        })
        .max(50, {
          message: getLocalizedValidationMessage(
            firstNameField,
            constants.VALIDATION_TYPES.MAX_LENGTH,
            {
              maxLength: 50,
            },
          ),
        }),
      lastName: z
        .string({
          message: getLocalizedValidationMessage(
            firstNameField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .nonempty(
          getLocalizedValidationMessage(
            firstNameField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        )
        .min(3, {
          message: getLocalizedValidationMessage(
            firstNameField,
            constants.VALIDATION_TYPES.MIN_LENGTH,
            {
              minLength: 3,
            },
          ),
        })
        .max(50, {
          message: getLocalizedValidationMessage(
            firstNameField,
            constants.VALIDATION_TYPES.MAX_LENGTH,
            {
              maxLength: 50,
            },
          ),
        }),
      temporaryPassword: z
        .string({
          message: getLocalizedValidationMessage(
            temporaryPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .nonempty(
          getLocalizedValidationMessage(
            temporaryPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        ),
      newPassword: z
        .string({
          message: getLocalizedValidationMessage(
            newPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .nonempty(
          getLocalizedValidationMessage(
            newPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        )
        .min(8, {
          message: getLocalizedValidationMessage(
            newPasswordField,
            constants.VALIDATION_TYPES.MIN_LENGTH,
            {
              minLength: 8,
            },
          ),
        })
        .regex(/[A-Z]/, {
          message: getLocalizedValidationMessage(
            newPasswordField,
            constants.VALIDATION_TYPES.CONTAINS_UPPERCASE,
          ),
        })
        .regex(/[a-z]/, {
          message: getLocalizedValidationMessage(
            newPasswordField,
            constants.VALIDATION_TYPES.CONTAINS_LOWERCASE,
          ),
        })
        .regex(/[0-9]/, {
          message: getLocalizedValidationMessage(
            newPasswordField,
            constants.VALIDATION_TYPES.CONTAINS_NUMBER,
          ),
        })
        .regex(/[^A-Za-z0-9]/, {
          message: getLocalizedValidationMessage(
            newPasswordField,
            constants.VALIDATION_TYPES.CONTAINS_SPECIAL,
          ),
        }),
      confirmPassword: z
        .string({
          message: getLocalizedValidationMessage(
            confirmPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        })
        .nonempty(
          getLocalizedValidationMessage(
            confirmPasswordField,
            constants.VALIDATION_TYPES.REQUIRED,
          ),
        ),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: getLocalizedValidationMessage(
        passwordsField,
        constants.VALIDATION_TYPES.MATCH,
      ),
    });
};
